import React from 'react'
import styled from 'react-emotion'

import config from 'config'
import storage from 'util/storage'
import { propFunc } from 'util/functions'

import { doOpenMainPage } from 'actions/pages'
import { SETTINGS_PAGE, STORYBOOK_PAGE } from 'constants/pages'
import { LANDING_PAGE as KB_LANDING_PAGE } from 'subapps/kb/pages'
import { CHAT_PAGE as CHAT_LANDING_PAGE } from 'subapps/chat/pages'
import { REPORTS_MAIN_PAGE } from 'ducks/reports/pages'
import { CONTACTS_PAGE } from 'ducks/crm/pages'

import themeVars from 'ui_theme/site/globals/site.variables'
import SpacedItems from 'shared/components/ui/SpacedItems'
import SUI from 'shared/components/ui/SUI'

import logo from 'assets/lettermark.svg'
import logoChristmas from 'assets/lettermark-chrismas.png'

import GlobalNavItem from './GlobalNavItem'
import UserItem from './UserItem'
import InboxLayoutSwitcher from './InboxLayoutSwitcher'
import MenuButton from './MenuButton'

const GlobalNavContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-bottom: ${themeVars.big};
  background: ${themeVars.white};
`

const FullHeightSpacedItemsContainer = styled(SpacedItems.Container)`
  height: 100%;
  min-height: 0;
`

const ScrollingSpacedItemsContainer = styled(SpacedItems.Container)`
  height: 100%;
  overflow-y: auto;
`

const FlexSpacedItem = styled(SpacedItems.Item)`
  min-height: 0;
  margin-bottom: ${themeVars.mini};
`

const StyledUserItem = styled(UserItem)`
  margin-top: 8px;
  margin-left: 8px;
  margin-right: 8px;
`

const Separator = styled.hr`
  width: 100%;
  border: 0;
  border-bottom: 1px solid ${themeVars.dust};
  margin: 0;
  padding: 0;
`

const StyledInboxLayoutSwitcher = styled(InboxLayoutSwitcher)`
  margin-top: auto;
`

// Javascript with 0 based months...wtf
// so 11 actually means 12 here
const isDecember = new Date().getMonth() === 11

const Logo = styled('img')`
  flex-shrink: 0;
  height: 48px;
  width: 48px;
  padding: ${isDecember ? 10 : 14}px;
`

const currentLogo = isDecember ? logoChristmas : logo

export default class GlobalNav extends React.Component {
  componentDidMount() {
    const { doSetLeftSidebarOpenState, active } = this.props
    const isOpen = this.getLeftSidebarState(active)
    doSetLeftSidebarOpenState(isOpen)
  }

  getLeftSidebarState = subapp => {
    const storedStates = storage.get('leftSidebarOpenStates')
    return storedStates && storedStates[subapp] !== undefined
      ? storedStates[subapp]
      : true
  }

  rememberLeftSidebarState = subapp => {
    const { isLeftSidebarCollapsed } = this.props
    const storedStates = storage.get('leftSidebarOpenStates')

    storage.set('leftSidebarOpenStates', {
      ...storedStates,
      [subapp]: !!isLeftSidebarCollapsed,
    })
  }

  handleSubappClick = subapp => () => {
    const { doSetLeftSidebarOpenState } = this.props
    const isOpen = this.getLeftSidebarState(subapp)
    doSetLeftSidebarOpenState(isOpen)
  }

  handleWidgetClick = () => {
    if (window.groove && window.groove.widget) {
      window.groove.widget.toggle()
    }
  }

  render() {
    const {
      active,
      isInInbox,
      isInChat,
      doShowHotkeysModal,
      noticeCounts,
    } = this.props
    const isLiveChatEnabled = storage.get('isLiveChatEnabled')
    return (
      <SUI>
        <GlobalNavContainer data-test-id="GlobalNav">
          <Logo src={currentLogo} />
          <Separator />
          <MenuButton
            onClick={propFunc(this.rememberLeftSidebarState, active)}
          />
          <Separator />
          <FullHeightSpacedItemsContainer direction="vertical" gap="none">
            <FlexSpacedItem key="apps">
              <ScrollingSpacedItemsContainer direction="vertical" gap="none">
                <SpacedItems.Item>
                  <GlobalNavItem
                    to={doOpenMainPage()}
                    title="Inbox"
                    iconName="inbox"
                    isActive={active === 'Inbox'}
                    onClick={propFunc(this.handleSubappClick, 'Inbox')}
                    noticeCount={noticeCounts.Inbox || null}
                  />
                </SpacedItems.Item>

                <SpacedItems.Item>
                  <GlobalNavItem
                    to={{ type: KB_LANDING_PAGE }}
                    title="Knowledge Base"
                    iconName="knowledgeBase"
                    isActive={active === 'Knowledge Base'}
                    onClick={propFunc(this.handleSubappClick, 'Knowledge Base')}
                    noticeCount={noticeCounts['Knowledge Base'] || null}
                  />
                </SpacedItems.Item>

                {(config.isDevelopment ||
                  config.isAlpha ||
                  isLiveChatEnabled) && (
                  <SpacedItems.Item>
                    <GlobalNavItem
                      to={{ type: CHAT_LANDING_PAGE }}
                      title="Live Chat"
                      iconName="liveChat"
                      isActive={active === 'Live Chat'}
                      onClick={propFunc(this.handleSubappClick, 'Live Chat')}
                      noticeCount={noticeCounts['Live Chat'] || null}
                    />
                  </SpacedItems.Item>
                )}

                <SpacedItems.Item>
                  <GlobalNavItem
                    to={{ type: REPORTS_MAIN_PAGE }}
                    title="Reporting"
                    iconName="reporting"
                    isActive={active === 'Reporting'}
                    onClick={propFunc(this.handleSubappClick, 'Reporting')}
                    noticeCount={noticeCounts.Reporting || null}
                  />
                </SpacedItems.Item>

                {localStorage.getItem('crm-enabled') && (
                  <SpacedItems.Item>
                    <GlobalNavItem
                      to={{ type: CONTACTS_PAGE }}
                      title="Address Book"
                      iconName="crm"
                      isActive={active === 'Address Book'}
                      onClick={propFunc(this.handleSubappClick, 'CRM')}
                    />
                  </SpacedItems.Item>
                )}

                {(config.isDevelopment || config.isAlpha) && (
                  <SpacedItems.Item>
                    <GlobalNavItem
                      to={{ type: STORYBOOK_PAGE }}
                      title="Storybook"
                      iconName="integrations"
                      isActive={active === 'Storybook'}
                      onClick={propFunc(this.handleSubappClick, 'Storybook')}
                      noticeCount={noticeCounts.Storybook || null}
                    />
                  </SpacedItems.Item>
                )}
              </ScrollingSpacedItemsContainer>
            </FlexSpacedItem>
            <SpacedItems.Item key="meta" gap="auto">
              <SpacedItems.Container direction="vertical" gap="none">
                {(isInInbox || isInChat) && (
                  <React.Fragment>
                    <SpacedItems.Item>
                      <Separator />
                    </SpacedItems.Item>

                    <SpacedItems.Item>
                      <StyledInboxLayoutSwitcher />
                    </SpacedItems.Item>

                    {isInInbox && (
                      <SpacedItems.Item>
                        <GlobalNavItem
                          title="Shortcuts"
                          iconName="shortcuts"
                          onClick={doShowHotkeysModal}
                          noticeCount={noticeCounts.Shortcuts || null}
                        />
                      </SpacedItems.Item>
                    )}
                  </React.Fragment>
                )}

                <SpacedItems.Item>
                  <Separator />
                </SpacedItems.Item>

                <SpacedItems.Item>
                  <GlobalNavItem
                    title="Get help"
                    iconName="question"
                    onClick={this.handleWidgetClick}
                  />
                </SpacedItems.Item>

                <SpacedItems.Item>
                  <GlobalNavItem
                    to={{ type: SETTINGS_PAGE }}
                    title="Settings"
                    iconName="settings"
                    isActive={active === 'Settings'}
                    onClick={propFunc(this.handleSubappClick, 'Settings')}
                    noticeCount={noticeCounts.Settings || null}
                  />
                </SpacedItems.Item>

                <SpacedItems.Item>
                  <StyledUserItem />
                </SpacedItems.Item>
              </SpacedItems.Container>
            </SpacedItems.Item>
          </FullHeightSpacedItemsContainer>
        </GlobalNavContainer>
      </SUI>
    )
  }
}
